'use client';

import { type JSX } from 'react';
import { twMerge } from 'tailwind-merge';

interface FeedbackPromptProps {
    onThumbUp: () => void;
    onThumbDown: () => void;
    disabled: boolean;
    selected?: 'up' | 'down';
}

export function FeedbackPrompt({ onThumbUp, onThumbDown, disabled, selected }: FeedbackPromptProps): JSX.Element {
    return (
        <div className="flex flex-col items-center gap-2">
            <p className="text-center text-xs font-normal leading-normal text-[#36414D]">
                Did the results meet your expectations?
            </p>
            <div className="flex items-center gap-2">
                <ThumbButton
                    onClick={onThumbUp}
                    disabled={disabled}
                    isSelected={selected === 'up'}
                    label="Rate response positively"
                    direction="up"
                />
                <ThumbButton
                    onClick={onThumbDown}
                    disabled={disabled}
                    isSelected={selected === 'down'}
                    label="Rate response negatively"
                    direction="down"
                />
            </div>
        </div>
    );
}

interface ThumbButtonProps {
    onClick: () => void;
    disabled: boolean;
    isSelected: boolean;
    label: string;
    direction: 'up' | 'down';
}

function ThumbButton({ onClick, disabled, isSelected, label, direction }: ThumbButtonProps): JSX.Element {
    return (
        <button
            type="button"
            aria-label={label}
            aria-pressed={isSelected}
            disabled={disabled}
            onClick={onClick}
            className={twMerge(
                'cursor-pointer rounded-md p-1 transition-colors',
                'text-[#61666B]',
                !isSelected && 'hover:text-[#36414D]',
                disabled && 'cursor-not-allowed opacity-50',
            )}
        >
            <ThumbIcon direction={direction} variant={isSelected ? 'filled' : 'outline'} />
        </button>
    );
}

const THUMB_OUTLINE_PATH =
    'M13.9999 5.33339C14.3555 5.33339 14.6666 5.46673 14.9333 5.73339C15.1999 6.00006 15.3333 6.31117 15.3333 6.66673V8.00006C15.3333 8.07784 15.3221 8.16117 15.2999 8.25006C15.2777 8.33895 15.2555 8.42228 15.2333 8.50006L13.2333 13.2001C13.1333 13.4223 12.9666 13.6112 12.7333 13.7667C12.4999 13.9223 12.2555 14.0001 11.9999 14.0001H4.66659V5.33339L8.66659 1.36673C8.83325 1.20006 9.03047 1.10284 9.25825 1.07506C9.48603 1.04728 9.70547 1.08895 9.91659 1.20006C10.1277 1.31117 10.2833 1.46673 10.3833 1.66673C10.4833 1.86673 10.5055 2.07228 10.4499 2.28339L9.69992 5.33339H13.9999ZM5.99992 5.90006V12.6667H11.9999L13.9999 8.00006V6.66673H7.99992L8.89992 3.00006L5.99992 5.90006ZM2.66659 14.0001C2.29992 14.0001 1.98603 13.8695 1.72492 13.6084C1.46381 13.3473 1.33325 13.0334 1.33325 12.6667V6.66673C1.33325 6.30006 1.46381 5.98617 1.72492 5.72506C1.98603 5.46395 2.29992 5.33339 2.66659 5.33339H4.66659V6.66673H2.66659V12.6667H4.66659V14.0001H2.66659Z';

const THUMB_FILLED_PATH =
    'M2.00008 10.6667C1.64453 10.6667 1.33341 10.5333 1.06675 10.2667C0.800081 10 0.666748 9.68889 0.666748 9.33333V8C0.666748 7.92222 0.675081 7.83889 0.691748 7.75C0.708415 7.66111 0.733415 7.57778 0.766748 7.5L2.76675 2.8C2.86675 2.57778 3.03341 2.38889 3.26675 2.23333C3.50008 2.07778 3.74453 2 4.00008 2H9.33342C9.70008 2 10.014 2.13056 10.2751 2.39167C10.5362 2.65278 10.6667 2.96667 10.6667 3.33333V10.1167C10.6667 10.2944 10.6306 10.4639 10.5584 10.625C10.4862 10.7861 10.389 10.9278 10.2667 11.05L6.65008 14.65C6.48342 14.8056 6.28619 14.9 6.05842 14.9333C5.83064 14.9667 5.61119 14.9278 5.40008 14.8167C5.18897 14.7056 5.03619 14.55 4.94175 14.35C4.8473 14.15 4.82786 13.9444 4.88342 13.7333L5.63342 10.6667H2.00008ZM13.3334 2C13.7001 2 14.014 2.13056 14.2751 2.39167C14.5362 2.65278 14.6667 2.96667 14.6667 3.33333V9.33333C14.6667 9.7 14.5362 10.0139 14.2751 10.275C14.014 10.5361 13.7001 10.6667 13.3334 10.6667C12.9667 10.6667 12.6529 10.5361 12.3917 10.275C12.1306 10.0139 12.0001 9.7 12.0001 9.33333V3.33333C12.0001 2.96667 12.1306 2.65278 12.3917 2.39167C12.6529 2.13056 12.9667 2 13.3334 2Z';

function ThumbIcon({ direction, variant }: { direction: 'up' | 'down'; variant: 'outline' | 'filled' }): JSX.Element {
    const path = variant === 'outline' ? THUMB_OUTLINE_PATH : THUMB_FILLED_PATH;
    const rotate = (variant === 'outline' && direction === 'down') || (variant === 'filled' && direction === 'up');
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className={rotate ? 'rotate-180' : undefined}
        >
            <path d={path} />
        </svg>
    );
}
